// Port of Weather/WeatherCache.swift: the latest forecast for each place, kept on the device for offline use.
//
// One record per key in IndexedDB (`kvStore('weather')`, the Swift JSON file per key); keys round the location to the
// 0.01° grid of the weather requests (WeatherLocationPrecision), so one transmitted cell is one key, and the elevation
// to 100 m. The index is loaded lazily on first use; records stored under another key than their forecast's key are
// re-keyed and older duplicates of a key are deleted, at load.
//
// Honesty rules: `fetchedAt` is never modified; an older forecast never replaces a newer one for the same key.

import type { EpochMs, GeoCoordinate, WeatherForecast } from '../core/models';
import { distance, isValidCoordinate } from '../core/geo';
import { kvStore, type KeyValueStore } from '../storage/idb';
import { LapseRate, roundHalfAwayFromZero, WeatherLocationPrecision } from './lapseRate';
import { WeatherFailure } from './weatherProvider';

function isForecast(value: unknown): value is WeatherForecast {
  if (typeof value !== 'object' || value === null) return false;
  const f = value as Record<string, unknown>;
  const location = f.location as Record<string, unknown> | undefined;
  return (
    typeof location === 'object' &&
    location !== null &&
    typeof location.latitude === 'number' &&
    typeof location.longitude === 'number' &&
    typeof f.fetchedAt === 'number' &&
    Array.isArray(f.hourly) &&
    Array.isArray(f.daily) &&
    typeof f.isSynthetic === 'boolean' &&
    typeof f.provenance === 'object' &&
    f.provenance !== null
  );
}

export class WeatherCache {
  /** Grid size (degrees) used to round locations in keys: the precision of the weather requests. */
  static readonly gridDegrees = WeatherLocationPrecision.cellDegrees;
  /** Step (metres) used to round elevations in keys. */
  static readonly elevationStep = 100;
  /** A cached forecast more than this many metres above/below the requested elevation is never returned. */
  static readonly maxElevationDifference = 1000;
  /** In nearest lookups, 1 m of elevation difference weighs like this many metres of horizontal distance. */
  static readonly elevationWeight = 5;
  /** Elevation differences up to this (metres) are served as-is, without lapse-rate adjustment. */
  static readonly elevationTolerance = 25;

  private index?: Map<string, WeatherForecast>;
  private unreadableKeys: string[] = [];
  private queue: Promise<unknown> = Promise.resolve();

  /** @param persistence Where forecasts are kept (default: IndexedDB store `weather`). */
  constructor(private readonly persistence: KeyValueStore<unknown> = kvStore<unknown>('weather')) {}

  /**
   * Cache key, e.g. `"46.50_7.67_1800"`; `"na"` stands for an unknown elevation. The location part is the location as
   * a weather request sends it.
   */
  static key(location: GeoCoordinate, elevation: number | undefined): string {
    let elevationPart = 'na';
    if (elevation !== undefined && Number.isFinite(elevation) && Math.abs(elevation) < 100_000) {
      const step = WeatherCache.elevationStep;
      elevationPart = String(roundHalfAwayFromZero(elevation / step) * step + 0);
    }
    const latitude = WeatherLocationPrecision.format(location.latitude);
    const longitude = WeatherLocationPrecision.format(location.longitude);
    return `${latitude}_${longitude}_${elevationPart}`;
  }

  /**
   * Stores `forecast` under its location/elevation key, unless a newer forecast is already cached there.
   * @throws WeatherFailure `noData` for an invalid location; the storage error otherwise.
   */
  store(forecast: WeatherForecast): Promise<void> {
    return this.serial(async () => {
      if (!isValidCoordinate(forecast.location)) throw new WeatherFailure({ kind: 'noData' });
      const entries = await this.loadIndex();
      const key = WeatherCache.key(forecast.location, forecast.elevation);
      const existing = entries.get(key);
      if (existing && existing.fetchedAt > forecast.fetchedAt) return;
      await this.persistence.set(key, forecast);
      // Value semantics (Swift structs): the cache keeps its own copy and hands out copies.
      entries.set(key, structuredClone(forecast));
    });
  }

  /**
   * The nearest cached forecast within `maxDistanceMeters` (and within `maxElevationDifference` when both elevations
   * are known), ranked by horizontal distance + `elevationWeight` × elevation difference, newest first on ties. When
   * the chosen forecast was made for a different elevation, temperatures are moved with the standard lapse rate and
   * `elevation` becomes the requested one; `fetchedAt`, `modelElevation`, `location` and provenance are preserved.
   */
  forecast(
    location: GeoCoordinate,
    elevation: number | undefined,
    maxDistanceMeters = 3000,
  ): Promise<WeatherForecast | undefined> {
    return this.serial(async () => {
      if (!isValidCoordinate(location)) return undefined;
      const requested = elevation !== undefined && Number.isFinite(elevation) ? elevation : undefined;
      let best: { forecast: WeatherForecast; score: number } | undefined;
      for (const candidate of (await this.loadIndex()).values()) {
        const d = distance(location, candidate.location);
        if (!(d <= maxDistanceMeters)) continue;
        let score = d;
        if (requested !== undefined && candidate.elevation !== undefined) {
          const difference = Math.abs(requested - candidate.elevation);
          if (difference > WeatherCache.maxElevationDifference) continue;
          score += difference * WeatherCache.elevationWeight;
        }
        const tieNotNewer = best && score === best.score && candidate.fetchedAt <= best.forecast.fetchedAt;
        if (best && (score > best.score || tieNotNewer)) continue;
        best = { forecast: candidate, score };
      }
      return best ? structuredClone(WeatherCache.adjusted(best.forecast, requested)) : undefined;
    });
  }

  /** Deletes every forecast fetched before `date`, plus unreadable records. */
  purge(olderThan: EpochMs): Promise<void> {
    return this.serial(async () => {
      const entries = await this.loadIndex();
      for (const [key, forecast] of [...entries]) {
        if (forecast.fetchedAt < olderThan) {
          await this.remove(key);
          entries.delete(key);
        }
      }
      for (const key of this.unreadableKeys) await this.remove(key);
      this.unreadableKeys = [];
    });
  }

  /** All cached forecasts, newest first. */
  all(): Promise<WeatherForecast[]> {
    return this.serial(async () =>
      [...(await this.loadIndex()).values()].sort((a, b) => b.fetchedAt - a.fetchedAt).map((f) => structuredClone(f)),
    );
  }

  // MARK: Adjustment

  /** Moves temperatures from the forecast's elevation to `target` using the standard lapse rate. */
  static adjusted(forecast: WeatherForecast, target: number | undefined): WeatherForecast {
    const source = forecast.elevation;
    if (target === undefined || source === undefined) return forecast;
    if (!(Math.abs(target - source) > WeatherCache.elevationTolerance)) return forecast;
    const delta = LapseRate.adjust(0, source, target);
    return {
      ...forecast,
      elevation: target,
      hourly: forecast.hourly.map((s) => ({
        ...s,
        temperature: s.temperature + delta,
        ...(s.feelsLike !== undefined ? { feelsLike: s.feelsLike + delta } : {}),
      })),
      daily: forecast.daily.map((d) => ({
        ...d,
        ...(d.temperatureMin !== undefined ? { temperatureMin: d.temperatureMin + delta } : {}),
        ...(d.temperatureMax !== undefined ? { temperatureMax: d.temperatureMax + delta } : {}),
      })),
    };
  }

  // MARK: Storage

  /** Runs operations one after another (the Swift actor's isolation). */
  private serial<T>(work: () => Promise<T>): Promise<T> {
    const next = this.queue.then(work, work);
    this.queue = next.catch(() => undefined);
    return next;
  }

  private async loadIndex(): Promise<Map<string, WeatherForecast>> {
    if (this.index) return this.index;
    let rows: Array<[string, unknown]> = [];
    try {
      rows = await this.persistence.entries();
    } catch (error) {
      console.error('Weather cache unreadable', error);
    }
    const entries = new Map<string, { forecast: WeatherForecast; storedKey: string }>();
    const superseded: string[] = [];
    for (const [storedKey, value] of rows) {
      if (!isForecast(value)) {
        this.unreadableKeys.push(storedKey);
        continue;
      }
      const key = WeatherCache.key(value.location, value.elevation);
      const existing = entries.get(key);
      if (existing) {
        // Two records for one key only happen after the key grid changed: keep the newer forecast.
        if (existing.forecast.fetchedAt >= value.fetchedAt) {
          superseded.push(storedKey);
          continue;
        }
        superseded.push(existing.storedKey);
      }
      entries.set(key, { forecast: value, storedKey });
    }
    await this.normalizeKeys(entries, superseded);
    this.index = new Map([...entries].map(([key, e]) => [key, e.forecast]));
    return this.index;
  }

  /**
   * Makes every indexed forecast live under its key and deletes superseded duplicates. Old keys are removed before
   * the new ones are written, because an old key can be another forecast's new key.
   */
  private async normalizeKeys(
    entries: Map<string, { forecast: WeatherForecast; storedKey: string }>,
    superseded: string[],
  ): Promise<void> {
    for (const key of superseded) await this.remove(key);
    const misfiled = [...entries].filter(([key, e]) => e.storedKey !== key);
    if (misfiled.length === 0) return;
    for (const [, e] of misfiled) await this.remove(e.storedKey);
    for (const [key, e] of misfiled) {
      try {
        await this.persistence.set(key, e.forecast);
      } catch (error) {
        // Kept in memory for this session; the forecast can be downloaded again.
        console.error('Could not re-key weather cache record', error);
      }
    }
  }

  private async remove(key: string): Promise<void> {
    try {
      await this.persistence.delete(key);
    } catch (error) {
      console.error('Could not remove weather cache record', error);
    }
  }
}
